import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { procurementFormSchema, ProcurementFormValues } from "../schema";
import { useCreateProcurement } from "@/hooks/use-procurement";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { CreateProcurementItemInput } from "@/types/database.types";

export const useProcurementForm = (harvestId?: string | null) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const createProcurementMutation = useCreateProcurement();

  // Fetch harvest data if harvestId is provided
  const { data: harvest, isLoading: isLoadingHarvest } = useQuery({
    queryKey: ['harvest', harvestId],
    queryFn: async () => {
      if (!harvestId) return null;
      
      const { data, error } = await supabase
        .from('harvests')
        .select(`
          *,
          farms (
            farmer_name,
            product_name
          )
        `)
        .eq('id', harvestId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!harvestId,
  });

  const form = useForm<ProcurementFormValues>({
    resolver: zodResolver(procurementFormSchema),
    defaultValues: {
      source_type: "farm",
      source_id: "",
      items: [{
        product_id: "",
        product_name: "",
        unit_of_measure: "kg",
        price_per_unit: 0,
        quantity: 0,
        quality_grade: "",
        notes: ""
      }],
      transport_cost_per_unit: 0,
      loading_unloading_cost_per_unit: 0,
      agent_cost_per_unit: 0,
      other_cost_per_unit: 0,
      vat_applicable: false,
      payment_term_type: "upfront",
      payment_term_days: 0,
      supports_damage_return: false,
      damage_return_percentage: 0,
      has_advance_payment: false,
      advance_payment_amount: 0,
      remaining_payment_amount: 0,
      use_different_bank: false,
      bank_name: "",
      bank_account: "",
      bank_branch: "",
      account_holder_name: "",
    },
  });

  const onSubmit = async (values: ProcurementFormValues) => {
    if (!user) {
      toast({
        title: "Authentication Error",
        description: "You must be logged in to create a procurement.",
        variant: "destructive",
      });
      return;
    }

    try {
      console.log("Submitting procurement with values:", values);

      // Calculate total amount from all items
      const totalAmount = values.items.reduce((total, item) => {
        return total + (item.price_per_unit * item.quantity);
      }, 0);

      // Calculate total quantity from all items
      const totalQuantity = values.items.reduce((total, item) => {
        return total + item.quantity;
      }, 0);

      // Convert form items to CreateProcurementItemInput format
      const itemsForCreation: CreateProcurementItemInput[] = values.items.map(item => ({
        product_id: item.product_id,
        product_name: item.product_name,
        price_per_unit: item.price_per_unit,
        quantity: item.quantity,
        quality_grade: item.quality_grade,
        notes: item.notes,
      }));

      // Determine initial status based on payment terms
      // Upfront: pending_finance → pending_quality → pending_warehouse → completed
      // Term: pending_quality → pending_warehouse → pending_finance → completed
      const initialStatus = values.payment_term_type === "upfront" ? "pending_finance" : "pending_quality";

      // Prepare main procurement data (only fields that belong in procurements table)
      const procurementData = {
        source_type: values.source_type,
        source_id: values.source_id,
        total_amount: totalAmount,
        total_quantity: totalQuantity,
        item_count: values.items.length,
        expected_delivery_date: values.expected_delivery_date?.toISOString().split('T')[0],
        notes: values.notes,
        requestor_id: user.id,
        status: initialStatus,
        // For backward compatibility, include first item's data
        product_name: values.items[0]?.product_name,
        quality_grade: values.items[0]?.quality_grade,
        items: itemsForCreation
      };

      // Prepare cost data for procurement_costs table
      const costsData = {
        price_per_unit: values.items[0]?.price_per_unit || 0, // Use first item's price as base
        transport_cost_per_unit: values.transport_cost_per_unit,
        loading_unloading_cost_per_unit: values.loading_unloading_cost_per_unit,
        agent_cost_per_unit: values.agent_cost_per_unit,
        other_cost_per_unit: values.other_cost_per_unit,
        other_cost_description: values.other_cost_description,
        vat_applicable: values.vat_applicable,
      };

      // Prepare finance data for procurement_finance table
      const financeData = {
        payment_term_type: values.payment_term_type,
        payment_term_days: values.payment_term_days,
        payment_due_date: values.payment_due_date?.toISOString().split('T')[0],
        supports_damage_return: values.supports_damage_return,
        damage_return_percentage: values.damage_return_percentage,
        has_advance_payment: values.has_advance_payment,
        advance_payment_amount: values.advance_payment_amount,
        remaining_payment_amount: values.remaining_payment_amount,
        use_different_bank: values.use_different_bank,
        bank_name: values.use_different_bank ? values.bank_name : null,
        bank_account: values.use_different_bank ? values.bank_account : null,
        bank_branch: values.use_different_bank ? values.bank_branch : null,
        account_holder_name: values.use_different_bank ? values.account_holder_name : null,
      };

      console.log("Main procurement data:", procurementData);
      console.log("Costs data:", costsData);
      console.log("Finance data:", financeData);

      // Pass all data to the service
      await createProcurementMutation.mutateAsync({
        ...procurementData,
        costs: costsData,
        finance: financeData
      });

      toast({
        title: "Success",
        description: "Procurement created successfully.",
      });

      navigate("/procurement");
    } catch (error) {
      console.error("Error creating procurement:", error);
      toast({
        title: "Error",
        description: "Failed to create procurement. Please try again.",
        variant: "destructive",
      });
    }
  };

  return {
    form,
    harvest,
    isLoadingHarvest,
    onSubmit,
    isSubmitting: createProcurementMutation.isPending,
  };
};
